import React from "react";
import { DisplayFields, PositionData } from "./types";

/* number formatting */
function formatShort(n?: number): string {
    if (n == null || isNaN(n)) return "—";
    const abs = Math.abs(n);
    if (abs >= 1e12) return `${(n / 1e12).toFixed(3)}T`;
    if (abs >= 1e9)  return `${(n / 1e9).toFixed(3)}B`;
    if (abs >= 1e6)  return `${(n / 1e6).toFixed(3)}M`;
    if (abs >= 1e3)  return `${(n / 1e3).toFixed(3)}K`;
    return n.toFixed(3);
}
const formatMoney = (n?: number) => `$${formatShort(n)}`;
const formatPct   = (n?: number) => (n == null || isNaN(n) ? "—" : `${n.toFixed(2)}%`);

/* fallback derivation if backend deltas aren't present */
function deriveDeltaPct(curr?: number, prev?: number) {
    if (curr == null || prev == null) return { d: undefined, p: undefined };
    const d = curr - prev;
    const p = prev === 0 ? undefined : (d / Math.abs(prev)) * 100;
    return { d, p };
}

/** background heatmap + overrides */
export function bgFromPercent(
    change?: number,
    opts?: { isNew?: boolean; isExited?: boolean }
): string {
    const { isNew = false, isExited = false } = opts ?? {};
    if (isNew)    return "rgba(34,197,94,0.22)";
    if (isExited) return "rgba(239,68,68,0.22)";
    if (change == null || isNaN(change)) return "rgba(255,255,255,0.03)";
    if (change < -75)  return "rgba(239,68,68,0.28)";
    if (change < -25)  return "rgba(239,68,68,0.18)";
    if (change < 0)    return "rgba(239,68,68,0.10)";
    if (change === 0)  return "rgba(255,255,255,0.03)";
    if (change < 25)   return "rgba(34,197,94,0.10)";
    if (change < 75)   return "rgba(34,197,94,0.18)";
    return "rgba(34,197,94,0.28)";
}

function Badge({
                   text,
                   bg,
                   fg = "#0b0b0b",
                   title,
               }: {
    text: string;
    bg: string;
    fg?: string;
    title?: string;
}) {
    return (
        <span
            title={title ?? text}
            style={{
                display: "inline-block",
                fontSize: 10.5,
                fontWeight: 800,
                padding: "2px 7px",
                borderRadius: 999,
                backgroundColor: bg,
                color: fg,
                lineHeight: "14px",
                whiteSpace: "nowrap",
            }}
        >
      {text}
    </span>
    );
}

/* STRICT: only use backend weight; never infer or rescale */
function sharePctFromBackendWeight(pos: PositionData): number | undefined {
    const w = pos.weight;
    if (typeof w !== "number" || Number.isNaN(w)) return undefined;

    // Use as-is: backend delivers percentage (e.g. 0.968 -> 0.968%).
    let pct = w;

    // sanity clamps
    if (pct < 0) return undefined;
    if (pct > 100000) return undefined;
    return pct;
}

export default function PositionCell({
                                         pos,
                                         prevPos,
                                         showPositionShare,
                                         displayFields,
                                         columnTotal, // ignored
                                         fill,
                                         isAggregatedRow, // unused
                                         isNew = false,
                                         isExited = false,
                                         amendFlag,
                                     }: {
    pos?: PositionData;
    prevPos?: PositionData | null;
    showPositionShare: boolean;
    displayFields: DisplayFields;
    columnTotal?: number; // ignored
    fill?: boolean;
    isAggregatedRow?: boolean;
    isNew?: boolean;
    isExited?: boolean;
    amendFlag?: PositionData["amendFlag"];
}) {
    // --------- EMPTY CELL ---------
    if (!pos) {
        return (
            <div
                style={{
                    height: "100%",
                    width: "100%",
                    background: "rgba(255,255,255,0.03)",
                    color: "#aaa",
                    padding: 12,
                    borderRadius: 10,
                    border: "1px solid rgba(255,255,255,0.06)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontStyle: "italic",
                    fontSize: 12,
                    boxSizing: "border-box",
                }}
            >
                —
            </div>
        );
    }

    // --------- NUMBERS / DELTAS ---------
    const value  = Number(pos.value ?? 0);
    const amount = pos.amount != null ? Number(pos.amount) : undefined;

    const prevValue  = prevPos?.value  != null ? Number(prevPos.value)  : undefined;
    const prevAmount = prevPos?.amount != null ? Number(prevPos.amount) : undefined;

    // Prefer backend deltas if they exist
    const apiValDelta         = pos.qoqValueChange         ?? pos.valueDelta;
    const apiValDeltaPercent  = pos.qoqValueChangePercent  ?? pos.valueDeltaPercent;
    const apiAmtDelta         = pos.qoqAmountChange        ?? pos.amountDelta;
    const apiAmtDeltaPercent  = pos.qoqAmountChangePercent ?? pos.amountDeltaPercent;

    const { d: valueDeltaDerived,  p: valueDeltaPercentDerived }  = deriveDeltaPct(value,  prevValue);
    const { d: amountDeltaDerived, p: amountDeltaPercentDerived } = deriveDeltaPct(amount, prevAmount);

    const valueDelta         = typeof apiValDelta        === "number" ? apiValDelta        : valueDeltaDerived;
    const valueDeltaPercent  = typeof apiValDeltaPercent === "number" ? apiValDeltaPercent : valueDeltaPercentDerived;
    const amountDelta        = typeof apiAmtDelta        === "number" ? apiAmtDelta        : amountDeltaDerived;
    const amountDeltaPercent = typeof apiAmtDeltaPercent === "number" ? apiAmtDeltaPercent : amountDeltaPercentDerived;

    // STRICT: use only backend weight for share %
    const sharePct = showPositionShare ? sharePctFromBackendWeight(pos) : undefined;

    // bg shading for cell
    const bg = fill ? bgFromPercent(valueDeltaPercent, { isNew, isExited }) : "transparent";

    // --------- AMENDMENT BADGE ---------
    const amendStyle: Record<
        NonNullable<PositionData["amendFlag"]>,
        { bg: string; fg: string; title: string }
    > = {
        ADDED:         { bg: "#34d399", fg: "#062012", title: "Opened / increased disclosure this quarter" },
        REMOVED:       { bg: "#fca5a5", fg: "#2a0a0a", title: "Closed / removed this quarter" },
        CHANGED:       { bg: "#fcd34d", fg: "#261c00", title: "Holdings materially changed" },
        UNCHANGED:     { bg: "rgba(255,255,255,0.10)", fg: "#eaeaea", title: "Unchanged vs prior active snapshot" },
        RECLASSIFIED:  { bg: "#a78bfa", fg: "#1f143a", title: "Reclassified (e.g. moved class / bucket)" },
        RECALCULATED:  { bg: "#93c5fd", fg: "#0c1a2e", title: "Recalculated / corrected values" },
    };

    const showAmend =
        !!amendFlag &&
        amendFlag !== "UNCHANGED" &&
        amendStyle[amendFlag as NonNullable<PositionData["amendFlag"]>] != null;

    // --------- SMALL CELL RENDER HELPERS ---------
    const cellStyle: React.CSSProperties = { whiteSpace: "nowrap", minHeight: 18 };
    const label = (t: string) => <span style={{ opacity: 0.85 }}>{t}</span>;

    const L1 = displayFields.valuePercent ? (
        <div style={cellStyle}>
            {label("Value %:")} <strong>{formatPct(valueDeltaPercent)}</strong>
        </div>
    ) : <div />;

    const R1 = displayFields.amountPercent ? (
        <div style={{ ...cellStyle, textAlign: "right" }}>
            {label("Amount %:")} <strong>{formatPct(amountDeltaPercent)}</strong>
        </div>
    ) : <div />;

    const L2 = displayFields.valueDelta ? (
        <div style={cellStyle}>
            {label("Value Δ:")} <strong>{formatMoney(valueDelta)}</strong>
        </div>
    ) : <div />;

    const R2 = displayFields.amountDelta ? (
        <div style={{ ...cellStyle, textAlign: "right" }}>
            {label("Amount Δ:")} <strong>{formatShort(amountDelta)}</strong>
        </div>
    ) : <div />;

    const L3 = showPositionShare && sharePct != null ? (
        <div style={cellStyle}>
            <em>{sharePct.toFixed(6)}% of total</em>
        </div>
    ) : <div />;

    const R3 = (
        <div style={{ ...cellStyle, textAlign: "right" }}>
            {label("Amount:")} <strong>{formatShort(amount)}</strong>
        </div>
    );

    // --------- RENDER ---------
    return (
        <div
            style={{
                height: "100%",
                width: "100%",
                backgroundColor: bg,
                color: "#fff",
                padding: 12,
                borderRadius: 10,
                border: "1px solid rgba(255,255,255,0.06)",
                display: "flex",
                flexDirection: "column",
                gap: 8,
                lineHeight: 1.15,
                boxSizing: "border-box",
            }}
        >
            {/* Top row */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    minHeight: 18,
                    flexWrap: "wrap",
                }}
            >
                <div style={{ fontWeight: 900, fontSize: 13.5 }}>Value:</div>
                <div style={{ fontWeight: 900, fontSize: 13.5 }}>{formatMoney(value)}</div>

                <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                    {showAmend && amendFlag && (
                        <Badge
                            text={amendFlag}
                            bg={amendStyle[amendFlag as NonNullable<PositionData["amendFlag"]>].bg}
                            fg={amendStyle[amendFlag as NonNullable<PositionData["amendFlag"]>].fg}
                            title={amendStyle[amendFlag as NonNullable<PositionData["amendFlag"]>].title}
                        />
                    )}
                    {isExited && <Badge text="EXITED" bg="#fca5a5" fg="#2a0a0a" title="Exited position" />}
                    {!isExited && isNew && <Badge text="NEW" bg="#34d399" fg="#062012" title="New position" />}
                </div>
            </div>

            <div style={{ height: 1, background: "rgba(255,255,255,0.08)" }} />

            {/* 3 rows × 2 cols */}
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
                    columnGap: 14,
                    rowGap: 4,
                    fontSize: 12,
                    alignItems: "baseline",
                }}
            >
                {L1}
                {R1}
                {L2}
                {R2}
                {L3}
                {R3}
            </div>
        </div>
    );
}
